<template>
  <div class="funding-queue">
    <app-cells v-if="recipient === 'ALTURA_EXPEDITIONS'">
      <app-filters v-model="alturaRelationship" :filters="alturaDirections" name="altura-funding-direction" @change="changeDirection" />
    </app-cells>
    <app-cells v-if="activeRelationship && relationshipsFailed">
      <span>Unable to load funding direction</span>
      <app-button @click.native="fetchRelationships">Retry</app-button>
    </app-cells>
    <app-cells v-else-if="activeRelationship">
      <span>{{ direction ? relationshipName(direction) : (relationshipsLoading ? 'Loading funding direction…' : 'Funding direction is not configured') }}</span>
      <span v-if="direction && !direction.active">This funding direction is inactive</span>
    </app-cells>
    <app-cells>
      <app-button :disabled="!canCreate" @click.native="$refs.fundingModal.openProposal()">Propose Tours Funding</app-button>
      <app-input v-model="search" class="input--300" type="search" placeholder="Search tours" aria-label="Search tours" @input="onSearch" />
      <app-filters
        v-model="availability"
        :filters="availabilityFilters"
        name="funding-availability"
        @change="changePage(1)"
      />
    </app-cells>
    <app-spinner v-if="loading && !loaded" />
    <app-cells v-else-if="failed">
      <span>Unable to load tours</span>
      <app-button @click.native="fetchItems">Retry</app-button>
    </app-cells>
    <template v-else>
      <funding-tours-table
        :items="items"
        :selected="selectedIds"
        :coverage="!!activeRelationship"
        :class="{ 'table-default--loading': loading }"
        :aria-busy="loading"
        selectable
        @select="selectTour"
        @select-all="selectAll"
      />
      <funding-pagination :page="page" :count="count" :length="items.length" @change="changePage" />
    </template>
    <div v-if="selected.length" class="flying-block flying-block--show funding-queue__selection">
      <div class="flying-block__text">Selected tours: {{ selected.length }} · {{ formatAmount(selectedAmount, selected[0].currency) }}</div>
      <app-button :disabled="loading || failed || !canCreate" @click.native="$refs.fundingModal.openSelection(selected)">Request funding</app-button>
    </div>
    <funding-modal ref="fundingModal" :recipient="recipient" :key="activeRelationship" :relationship="activeRelationship" :direction="direction" @created="onCreated" />
  </div>
</template>

<script>
import { getTransferPricingFundingQueue } from '@/http'
import FundingModal from './FundingModal'
import FundingPagination from './Pagination'
import FundingToursTable from './ToursTable'
import { canSelect, directionParams, formatAmount, groupId, relationshipName, selectedRows, sumFunding } from './funding'
import paginatedList from './paginatedList'
import relationships from './relationships'

export default {
  name: 'TransferPricingFundingQueue',
  components: { FundingModal, FundingPagination, FundingToursTable },
  mixins: [paginatedList, relationships],
  props: {
    recipient: { type: String, default: '' },
    relationship: { type: String, default: '' },
  },
  data() {
    return {
      selected: [],
      alturaRelationship: 'uae-to-altura-tz',
      alturaDirections: [
        { value: 'uae-to-altura-tz', label: 'Altezza Travel (UAE) → Altura' },
        { value: 'altezza-tz-to-altura-tz', label: 'Altezza Travelling (TZ) → Altura' },
      ],
      availability: 'all',
      availabilityFilters: [
        { value: 'all', label: 'All' },
        { value: 'available', label: 'Available' },
        { value: 'unavailable', label: 'Unavailable' },
      ],
    }
  },
  computed: {
    activeRelationship() { return this.relationship || this.alturaRelationship },
    direction() { return this.relationships.find(item => item.slug === this.activeRelationship) || null },
    canCreate() { return (!!this.direction && this.direction.active && !this.relationshipsFailed) },
    selectedIds() {
      return this.selected.map(row => groupId(row.group))
    },
    selectedAmount() {
      return sumFunding(selectedRows(this.selected), 'amount_usd')
    },
  },
  methods: {
    formatAmount,
    relationshipName,
    requestItems(params) {
      return getTransferPricingFundingQueue({ ...params, ...directionParams(this.activeRelationship), availability: this.availability })
    },
    changeDirection() {
      this.selected = []
      this.changePage(1)
    },
    onItemsLoaded() {
      this.items = this.items.map(row => selectedRows([row])[0])
      this.selected = this.selected.map(row => this.items.find(item => groupId(item.group) === groupId(row.group)) || row).filter(canSelect)
    },
    selectTour(row, checked) {
      if (this.loading || this.failed) return
      const id = groupId(row.group)
      if (!checked) {
        this.selected = this.selected.filter(item => groupId(item.group) !== id)
      } else if (canSelect(row) && !this.selectedIds.includes(id)) {
        if (this.selected.length && this.selected[0].currency !== row.currency) {
          this.$notify({ type: 'error', title: 'Select tours in the same currency' })
          return
        }
        this.selected = [...this.selected, row]
      }
    },
    selectAll(checked) {
      this.items.filter(canSelect).forEach(row => this.selectTour(row, checked))
    },
    onCreated(batch) {
      this.selected = []
      this.fetchItems()
      this.$router.push({ name: 'transfer-pricing-funding-detail', params: { id: batch.id } })
    },
  },
}
</script>

<style lang="sass" scoped>
.funding-queue__selection
  max-width: calc(100vw - 32px)
  min-width: 0
  flex-wrap: wrap
  gap: 12px 20px
</style>
